import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { useLocation, useNavigate } from "react-router-dom";
import PencomTextField from "@/components/input/PencomTextField";
import { useInitiateAuth, useVerifyLoginOTP } from "@/hooks/auth/auth.hook";
import { useRequestContactUpdateLink } from "@/hooks/correction-link/correction-link.hook";
import { toast } from "@/utils/toast";
import { useForm } from "@tanstack/react-form";
import { type OTPVerificationForm } from "@/forms/auth/auth.form";
import { PencomRoutes } from "@/router";
import { useAuthStore } from "@/stores/authStore";
import { useOTPRateLimiter } from "@/hooks/useOTPRateLimiter";
import { PencomBackButton } from "@/components/layout/PencomBackButton";
import { useEffect, useState } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ContactUpdateLinkSentDialog } from "@/components/onboarding/ContactUpdateLinkSentDialog";

const formatResendTimer = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const VerificationPage = () => {
    const navigate = useNavigate();
    const data = useLocation().state as {
        email: string | null;
        employerCode: string | null;
    };

    const authenticate = useAuthStore((state) => state.login);
    const contactEmail = data?.email;

    const verifyLoginOTPMutation = useVerifyLoginOTP();
    const initiateAuthMutation = useInitiateAuth();
    const requestContactUpdateLinkMutation = useRequestContactUpdateLink();
    const { canResend, timeRemaining, startCooldown } = useOTPRateLimiter(60, true);
    const { trackEvent, eventNames } = useAnalytics();

    const [isContactUpdateDialogOpen, setIsContactUpdateDialogOpen] = useState(false);
    const [resolvedContactEmail, setResolvedContactEmail] = useState("");

    const form = useForm({
        defaultValues: {
            otp: '',
        } as OTPVerificationForm,
        onSubmit: async ({ value }) => {
            verifyLoginOTPMutation.mutate({
                employerCode: data?.employerCode as string,
                otpCode: value.otp
            }, {
                //eslint-disable-next-line @typescript-eslint/no-explicit-any
                onSuccess: (response: any) => {
                    toast.success("OTP verified successfully");
                    trackEvent(eventNames.onboardingStepSubmitted, {
                        step: "otp_verification",
                        status: "success",
                    });
                    authenticate(response?.employer);
                    navigate(PencomRoutes.DASHBOARD, { replace: true });
                },
                onError: (error) => {
                    form.setFieldValue("otp", "");
                    trackEvent(eventNames.onboardingStepSubmitted, {
                        step: "otp_verification",
                        status: "error",
                        message: error.message,
                    });
                    toast.error(error.message || "Invalid or expired code. Please try again.");
                }
            });
        },
    });

    useEffect(() => {
        trackEvent(eventNames.onboardingPageView, {
            step: "otp_verification",
        });
    }, []);

    const handleResendOTP = () => {
        if (!canResend) return;
        if (!contactEmail) {
            toast.error('Contact information not available. Please try logging in again.');
            return;
        }
        if (!data?.employerCode) {
            toast.error('Employer ID not found. Please try logging in again.');
            return;
        }
        initiateAuthMutation.mutate({ employerCode: data.employerCode }, {
            onSuccess: () => {
                toast.success('OTP sent successfully');
                trackEvent(eventNames.onboardingStepSubmitted, {
                    step: "otp_verification",
                    action: "resend_otp",
                });
                startCooldown();
            },
            onError: (error) => {
                toast.error(error.message || 'Failed to send OTP. Please try again.');
            }
        });
    };

    const emailDisplay = contactEmail || 'your email';

    const handleChangeEmailClick = () => {
        if (!data?.employerCode) {
            toast.error('Employer ID not found. Please try logging in again.');
            return;
        }
        requestContactUpdateLinkMutation.mutate({ employerCode: data.employerCode }, {
            onSuccess: (response) => {
                setResolvedContactEmail(response.email);
                setIsContactUpdateDialogOpen(true);
            },
            onError: (error) => {
                toast.error(error.message || 'Unable to send the update link. Please try again.');
            },
        });
    };

    return (
        <div className="w-full flex flex-col">
            <PencomBackButton title='Back' />

            <div className='mt-19'>
                <h2 className="font-clash-display text-center font-semibold text-[40px] text-medium-dark-grey leading-[1.25] tracking-[-0.6px]">
                    Check your email
                </h2>
                <p className="font-clash-display text-center text-medium-dark-grey leading-[25px] tracking-[-0.08px] mt-3">
                    Enter the 6-digit code sent to <span className="font-semibold">{emailDisplay}</span>
                </p>
                <p className="font-clash-display text-center text-sm text-medium-grey tracking-[-0.07px] mt-1">
                    Not your email?{" "}
                    <button
                        type="button"
                        onClick={handleChangeEmailClick}
                        disabled={requestContactUpdateLinkMutation.isPending}
                        className="text-primary-button font-semibold hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Change email
                    </button>
                </p>

                <div className="flex flex-col gap-10 mt-10">
                    <form.Field
                        name="otp"
                        validators={{
                            onChange: ({ value }) => {
                                if (value && value.length > 0 && value.length !== 6) {
                                    return 'OTP must be 6 digits';
                                }
                                return undefined;
                            },
                        }}
                        children={(field) => (
                            <div className="flex flex-col gap-2">
                                <PencomTextField
                                    id="verification-code"
                                    label=""
                                    type="otp"
                                    value={field.state.value}
                                    onChange={(e) => {
                                        if (verifyLoginOTPMutation.error) verifyLoginOTPMutation.reset();
                                        field.handleChange(e.target.value);
                                        if (e.target.value.length === 6) {
                                            setTimeout(() => form.handleSubmit(), 0);
                                        }
                                    }}
                                    otpLength={6}
                                    otpMasked={true}
                                    otpMaskChar="•"
                                    otpMaskDelay={1500}
                                    error={
                                        field.state.meta.errors.length > 0 ||
                                        !!verifyLoginOTPMutation.error
                                    }
                                    helperText={
                                        field.state.meta.errors.length > 0
                                            ? (field.state.meta.errors[0] as string)
                                            : verifyLoginOTPMutation.error
                                                ? 'Invalid or expired code. Please try again.'
                                                : undefined
                                    }
                                />
                                {field.state.meta.errors.length > 0 && !verifyLoginOTPMutation.error && (
                                    <div className="flex items-center gap-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                        <p className="text-sm text-red-600 font-clash-display">{field.state.meta.errors[0]}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    />


                    <form.Subscribe
                        children={(state) => {
                            const isDisabled = !state.values.otp || state.values.otp.length !== 6 || verifyLoginOTPMutation.isPending;
                            return (
                                <PrimaryButton
                                    className="w-full"
                                    title={verifyLoginOTPMutation.isPending ? 'Verifying...' : 'Verify'}
                                    onClick={() => form.handleSubmit()}
                                    disabled={isDisabled}
                                    isLoading={verifyLoginOTPMutation.isPending}
                                />
                            );
                        }}
                    />

                    <div className="bg-white border border-stroke rounded-lg p-4 flex flex-col gap-2.5">
                        <div className="flex items-center gap-2">
                            <img src="/assets/icons/info-outline.svg" alt="" className="w-4 h-4 shrink-0" aria-hidden />
                            <p className="font-clash-display font-medium text-sm text-medium-dark-grey leading-[18px] tracking-[-0.07px]">
                                Didn&apos;t get code?
                            </p>
                        </div>
                        <p className="font-clash-display font-normal text-sm text-medium-grey leading-[18px] tracking-[-0.07px]">
                            Please check your SPAM or JUNK folder if you don&apos;t see it.
                            <br />
                            You can request a new code below.
                        </p>
                        <p className="font-clash-display font-normal text-xs text-medium-dark-grey leading-[25px] tracking-[-0.06px]">
                            {canResend && !initiateAuthMutation.isPending ? (
                                <button
                                    type="button"
                                    onClick={handleResendOTP}
                                    className="text-primary-button font-semibold hover:underline cursor-pointer"
                                >
                                    Resend the code
                                </button>
                            ) : (
                                <>Resend the code in {formatResendTimer(timeRemaining)}</>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            <ContactUpdateLinkSentDialog
                open={isContactUpdateDialogOpen}
                onOpenChange={setIsContactUpdateDialogOpen}
                email={resolvedContactEmail}
            />
        </div>
    );
};

export default VerificationPage;